import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { LabelPrinter, LookupRequest, LookupResponse } from "@pm-barcode/shared";
import type { ScannerEvent } from "../types/scanner.ts";
import { useApiClient } from "../hooks/use-api-client.ts";
import { useScanner } from "../hooks/use-scanner.ts";
import { RequestError } from "../api/client.ts";
import { canPrintOnDevice, printZplOnDevice } from "../lib/printer.ts";

/** Plain-language reason a request failed, for the operator. */
function failureMessage(err: unknown): string {
  if (err instanceof RequestError) return `${err.message} (${err.status})`;
  if (err instanceof TypeError) return "Couldn't reach the server — check the scanner's network connection.";
  return "The server sent an unexpected reply.";
}

/**
 * What the screen shows: the Worker's answer, or the lookup itself failing (server error,
 * timeout, no connection). Kept apart from "unknown" so a failure is never mistaken for an
 * unrecognised barcode — that hid a missing production rule on 2026-10-22.
 */
type LookupResult = LookupResponse | { outcome: "error"; rawBarcode: string; message: string };

// ─── Quality status badge ────────────────────────────────────────────────────

const QUALITY_COLOURS: Record<string, string> = {
  ready: "bg-green-700 text-green-100",
  quarantined: "bg-amber-700 text-amber-100",
  hold: "bg-red-700 text-red-100",
  return: "bg-orange-700 text-orange-100",
};

function QualityBadge({ status }: { status: string | null }) {
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : "Unknown";
  const colour = (status && QUALITY_COLOURS[status.toLowerCase()]) ?? "bg-gray-700 text-gray-300";
  return (
    <span className={`rounded-full px-3 py-0.5 text-sm font-semibold ${colour}`}>
      {label}
    </span>
  );
}

// ─── Result card ─────────────────────────────────────────────────────────────

// ─── Print a new label ───────────────────────────────────────────────────────

type PrintStatus =
  | { kind: "idle" }
  | { kind: "busy"; printer: string }
  | { kind: "done"; printer: string }
  | { kind: "error"; message: string };

/**
 * Reprint a found bag's label: the Worker gets the label from Odoo (exactly what Odoo prints)
 * and the scanner sends it to the chosen Zebra. Hidden until printers are set up.
 */
function PrintPanel({ lotId, printers }: { lotId: number; printers: LabelPrinter[] }) {
  const api = useApiClient();
  const [status, setStatus] = useState<PrintStatus>({ kind: "idle" });
  if (!printers.length) return null;

  async function printOn(printer: LabelPrinter) {
    if (!api) return;
    setStatus({ kind: "busy", printer: printer.name });
    try {
      const job = await api.printLabel({ lotId, printer: printer.key, deviceId: import.meta.env.VITE_DEVICE_ID });
      await printZplOnDevice(job.printer.host, job.printer.port, job.zpl);
      setStatus({ kind: "done", printer: printer.name });
    } catch (err) {
      setStatus({ kind: "error", message: err instanceof Error && !(err instanceof RequestError) && !(err instanceof TypeError) ? err.message : failureMessage(err) });
    }
  }

  return (
    <div className="mt-4 border-t border-gray-700 pt-4">
      <p className="mb-2 text-xs uppercase tracking-widest text-gray-500">Print a new label</p>
      {!canPrintOnDevice() ? (
        <p className="text-sm text-gray-400">Printing works in the scanner app (version 1.3.0 or later).</p>
      ) : (
        <div className="flex gap-3">
          {printers.map((p) => (
            <button
              key={p.key}
              onClick={() => void printOn(p)}
              disabled={status.kind === "busy"}
              className="flex-1 rounded-xl bg-blue-600 py-3 font-semibold active:bg-blue-700 disabled:opacity-40"
            >
              {status.kind === "busy" && status.printer === p.name ? "Printing…" : p.name}
            </button>
          ))}
        </div>
      )}
      {status.kind === "done" && <p className="mt-2 text-sm text-green-400">Label sent to {status.printer}.</p>}
      {status.kind === "error" && <p className="mt-2 text-sm text-red-400">{status.message}</p>}
    </div>
  );
}

function ResultCard({ result, printers }: { result: LookupResult; printers: LabelPrinter[] }) {
  if (result.outcome === "error") {
    return (
      <div className="rounded-2xl bg-gray-800 p-5">
        <p className="text-lg font-bold text-scan-error">Lookup failed</p>
        <p className="mt-2 text-sm text-gray-300">{result.message}</p>
        <p className="mt-2 text-sm text-gray-400">
          Scan again. If it keeps failing, tell a supervisor.
        </p>
        <p className="mt-3 break-all font-mono text-xs text-gray-600">{result.rawBarcode}</p>
      </div>
    );
  }

  if (result.outcome === "unknown") {
    return (
      <div className="rounded-2xl bg-gray-800 p-5">
        <p className="text-lg font-bold text-scan-error">Unknown Barcode</p>
        <p className="mt-2 text-sm text-gray-400">
          This barcode was not recognised. It has been logged for review.
        </p>
        <p className="mt-3 break-all font-mono text-xs text-gray-600">{result.rawBarcode}</p>
      </div>
    );
  }

  if (result.outcome === "not_in_odoo") {
    const n = result.normalised;
    return (
      <div className="rounded-2xl bg-gray-800 p-5">
        <p className="text-lg font-bold text-amber-400">Not Yet in Stock</p>
        <p className="mt-1 text-sm text-gray-400">
          Barcode decoded but this bag has not been received into Odoo yet.
        </p>
        <dl className="mt-4 space-y-2 text-sm">
          <Row label="Category" value={n.category.replace(/_/g, " ")} />
          <Row label="Batch" value={n.batch} />
          {n.bagNumber && <Row label="Bag" value={n.bagNumber} />}
          {n.weightKg > 0 && <Row label="Weight" value={`${n.weightKg} kg`} />}
          {n.bestBefore && <Row label="Best before" value={n.bestBefore} />}
        </dl>
      </div>
    );
  }

  // outcome === "found"
  const n = result.normalised;
  const { product, lot, locations } = result;

  return (
    <div className="rounded-2xl bg-gray-800 p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <p className="text-lg font-bold leading-tight">{product.name}</p>
        <QualityBadge status={lot.qualityStatus} />
      </div>

      <dl className="space-y-2 text-sm">
        <Row label="Lot / serial" value={lot.name} mono />
        <Row label="Batch" value={n.batch} />
        {n.bagNumber && <Row label="Bag" value={n.bagNumber} />}
        {n.weightKg > 0 && <Row label="Weight" value={`${n.weightKg} kg`} />}
        {n.bestBefore && <Row label="Best before" value={n.bestBefore} />}
        {lot.expiryDate && <Row label="Expiry" value={lot.expiryDate.split("T")[0] ?? lot.expiryDate} />}
      </dl>

      <div className="mt-4 border-t border-gray-700 pt-4">
        <p className="mb-2 text-xs uppercase tracking-widest text-gray-500">Stock location</p>
        {locations.length === 0 ? (
          <p className="text-sm text-gray-400">Not currently in stock</p>
        ) : (
          <ul className="space-y-1">
            {locations.map((loc) => (
              <li key={loc.id} className="flex justify-between text-sm">
                <span className="font-mono text-gray-300">{loc.name}</span>
                <span className="tabular-nums text-gray-400">{loc.quantity}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <PrintPanel key={lot.id} lotId={lot.id} printers={printers} />
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-right ${mono ? "font-mono" : ""} text-gray-200`}>{value}</dd>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BagLookup() {
  const navigate = useNavigate();
  const api = useApiClient();
  const search = useSearch({ strict: false }) as { code?: string; sym?: string; at?: string };
  const [result, setResult] = useState<LookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const scanInFlight = useRef(false);
  const [printers, setPrinters] = useState<LabelPrinter[]>([]);
  const [typing, setTyping] = useState(false);
  const [typedBatch, setTypedBatch] = useState("");
  const [typedBag, setTypedBag] = useState("");

  // Printers set up for this site; none (or an error) simply hides the print buttons.
  useEffect(() => {
    if (!api) return;
    api.listPrinters().then((r) => setPrinters(r.printers)).catch(() => setPrinters([]));
  }, [api]);

  // Find a bag by typed batch + bag number when its label won't scan.
  function runManualLookup() {
    const batch = typedBatch.trim();
    const bagNumber = typedBag.trim();
    if (!api || !batch || scanInFlight.current) return;
    scanInFlight.current = true;
    setLoading(true);
    setResult(null);
    const typed = bagNumber ? `${batch}-${bagNumber}` : batch;
    api
      .postManualLookup({ batch, ...(bagNumber ? { bagNumber } : {}), deviceId: import.meta.env.VITE_DEVICE_ID })
      .then((r) => {
        setResult(r);
        setTyping(false);
      })
      .catch((err: unknown) => setResult({ outcome: "error", rawBarcode: typed, message: failureMessage(err) }))
      .finally(() => {
        setLoading(false);
        scanInFlight.current = false;
      });
  }

  const runLookup = useCallback(
    (rawBarcode: string, symbology: ScannerEvent["symbology"], capturedAt: string) => {
      if (!api || scanInFlight.current) return;
      scanInFlight.current = true;
      setLoading(true);
      setResult(null);

      const body: LookupRequest = {
        rawBarcode,
        symbology,
        deviceId: import.meta.env.VITE_DEVICE_ID,
        capturedAt,
      };

      void api
        .postLookup(body)
        .then((r) => setResult(r))
        .catch((err: unknown) => {
          setResult({
            outcome: "error",
            rawBarcode,
            message: failureMessage(err),
          });
        })
        .finally(() => {
          setLoading(false);
          scanInFlight.current = false;
        });
    },
    [api],
  );

  useScanner((event) => runLookup(event.rawBarcode, event.symbology, event.capturedAt));

  // Resolve a scan handed in from the workflow menu, then clear it from the URL so a refresh
  // or back-navigation doesn't look it up again.
  const consumedRef = useRef(false);
  useEffect(() => {
    if (consumedRef.current || !search.code || !api) return;
    consumedRef.current = true;
    runLookup(
      search.code,
      (search.sym as ScannerEvent["symbology"]) ?? "other",
      search.at ?? new Date().toISOString(),
    );
    void navigate({ to: "/lookup", search: {}, replace: true });
  }, [search.code, search.sym, search.at, api, runLookup, navigate]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3">
        <button
          onClick={() => void navigate({ to: "/workflow" })}
          className="text-sm text-gray-400 active:text-white"
        >
          ←
        </button>
        <div className="flex-1">
          <p className="font-semibold">Bag Lookup</p>
          <p className="text-xs text-gray-500">Scan a bag, or type its batch and bag number</p>
        </div>
        <span className="rounded-full bg-amber-900/50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-amber-300">
          Lookup
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <p className="text-gray-400">Looking up…</p>
          </div>
        ) : result ? (
          <ResultCard result={result} printers={printers} />
        ) : (
          <div className="flex h-40 items-center justify-center">
            <p className="text-sm text-gray-500">Scan a bag to see its details</p>
          </div>
        )}

        {/* Typed lookup, for a label that won't scan */}
        {typing ? (
          <form
            className="mt-4 rounded-2xl bg-gray-800 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              runManualLookup();
            }}
          >
            <p className="mb-3 font-semibold">Find a bag by its numbers</p>
            <label className="mb-1 block text-xs text-gray-400">Batch number</label>
            <input
              value={typedBatch}
              onChange={(e) => setTypedBatch(e.target.value)}
              autoFocus
              className="mb-3 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-3 font-mono text-white focus:border-blue-500 focus:outline-none"
            />
            <label className="mb-1 block text-xs text-gray-400">Bag number (leave blank for 20 kg bags)</label>
            <input
              value={typedBag}
              onChange={(e) => setTypedBag(e.target.value)}
              inputMode="numeric"
              className="mb-4 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-3 font-mono text-white focus:border-blue-500 focus:outline-none"
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setTyping(false)}
                className="flex-1 rounded-xl bg-gray-700 py-3 font-semibold active:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!typedBatch.trim() || loading}
                className="flex-1 rounded-xl bg-blue-600 py-3 font-semibold active:bg-blue-700 disabled:opacity-40"
              >
                Find bag
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setTyping(true)}
            className="mt-4 w-full rounded-xl border border-gray-700 py-3 text-sm text-gray-300 active:bg-gray-800"
          >
            Can't scan it? Type batch and bag number
          </button>
        )}
      </div>
    </div>
  );
}
